import React, { useEffect, useState } from 'react';
import { TrendingUp, Activity, Target, Eye } from 'lucide-react';
import api from '@/lib/api';
import { NoData } from '@/components/NoData';
import { BarChart, Bar, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid, AreaChart, Area, PieChart, Pie, Cell, Legend } from 'recharts';

const COLORS = ['#00F0FF', '#E5FF00', '#00E676', '#FFD700'];

export default function Analytics() {
  const [s, setS] = useState(null);
  const [campaigns, setCampaigns] = useState([]);

  useEffect(() => {
    (async () => {
      const [a, c] = await Promise.all([api.get('/analytics/summary'), api.get('/campaigns')]);
      setS(a.data); setCampaigns(c.data);
    })();
  }, []);

  const platformBreakdown = React.useMemo(() => {
    const m = {};
    campaigns.forEach(c => { m[c.platform] = (m[c.platform] || 0) + (c.spend || 0); });
    return Object.entries(m).map(([name, value]) => ({ name, value: Math.round(value) }));
  }, [campaigns]);

  return (
    <div className="space-y-7" data-testid="analytics-root">
      <div>
        <div className="text-[10px] tracking-[0.25em] uppercase font-bold text-boosta-cyan mb-2">Analytics</div>
        <h1 className="font-display font-black text-4xl tracking-tighter">Performance intelligence</h1>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: 'Revenue', value: `£${(s?.revenue ?? 0).toLocaleString()}`, icon: TrendingUp, accent: 'text-boosta-emerald' },
          { label: 'Spend', value: `£${(s?.total_spend ?? 0).toLocaleString()}`, icon: Activity, accent: 'text-white' },
          { label: 'CTR', value: `${s?.ctr ?? 0}%`, icon: Eye, accent: 'text-boosta-cyan' },
          { label: 'Conversions', value: (s?.total_conversions ?? 0).toLocaleString(), icon: Target, accent: 'text-boosta-yellow' },
        ].map(k => (
          <div key={k.label} className="glass rounded-2xl p-5">
            <div className="flex justify-between items-center mb-2">
              <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-white/40">{k.label}</div>
              <k.icon className={`w-4 h-4 ${k.accent}`}/>
            </div>
            <div className="font-display font-black text-3xl">{k.value}</div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 glass rounded-2xl p-6">
          <div className="text-[10px] tracking-[0.25em] uppercase font-bold text-boosta-cyan mb-1">Daily trend</div>
          <div className="font-display font-bold text-xl mb-5">Spend & conversions</div>
          <div className="h-72">
            {s?.has_data && s?.trend?.length ? (
            <ResponsiveContainer>
              <AreaChart data={s?.trend || []}>
                <defs>
                  <linearGradient id="aSpend" x1="0" x2="0" y1="0" y2="1"><stop offset="0%" stopColor="#00F0FF" stopOpacity="0.4"/><stop offset="100%" stopColor="#00F0FF" stopOpacity="0"/></linearGradient>
                  <linearGradient id="aConv" x1="0" x2="0" y1="0" y2="1"><stop offset="0%" stopColor="#E5FF00" stopOpacity="0.5"/><stop offset="100%" stopColor="#E5FF00" stopOpacity="0"/></linearGradient>
                </defs>
                <CartesianGrid stroke="rgba(255,255,255,0.05)" strokeDasharray="3 3"/>
                <XAxis dataKey="day" stroke="rgba(255,255,255,0.3)" fontSize={11}/>
                <YAxis stroke="rgba(255,255,255,0.3)" fontSize={11}/>
                <Tooltip contentStyle={{ background: 'rgba(8,10,18,0.95)', border: '1px solid rgba(0,240,255,0.3)', borderRadius: 12 }} />
                <Area type="monotone" dataKey="spend" stroke="#00F0FF" fill="url(#aSpend)" strokeWidth={2}/>
                <Area type="monotone" dataKey="conversions" stroke="#E5FF00" fill="url(#aConv)" strokeWidth={2}/>
              </AreaChart>
            </ResponsiveContainer>
            ) : (
              <NoData title="No performance data yet"
                hint="Boosta only shows real results. Connect an ads-approved Meta account and launch a campaign — spend and conversions sync automatically." />
            )}
          </div>
        </div>
        <div className="glass rounded-2xl p-6">
          <div className="text-[10px] tracking-[0.25em] uppercase font-bold text-boosta-emerald mb-1">Channel mix</div>
          <div className="font-display font-bold text-xl mb-5">Spend by platform</div>
          {platformBreakdown.every(p => !p.value) ? (
            <div className="h-60"><NoData title="No spend recorded" hint="Platform mix appears once campaigns start spending." /></div>
          ) : (
            <div className="h-60">
              <ResponsiveContainer>
                <PieChart>
                  <Pie data={platformBreakdown} dataKey="value" nameKey="name" innerRadius={50} outerRadius={85} paddingAngle={3}>
                    {platformBreakdown.map((p, i) => <Cell key={p.name} fill={COLORS[i % COLORS.length]} stroke="none"/>)}
                  </Pie>
                  <Tooltip contentStyle={{ background: 'rgba(8,10,18,0.95)', border: '1px solid rgba(0,240,255,0.3)', borderRadius: 12 }}/>
                  <Legend wrapperStyle={{ fontSize: 11, color: 'rgba(255,255,255,0.6)' }}/>
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </div>

      <div className="glass rounded-2xl p-6">
        <div className="text-[10px] tracking-[0.25em] uppercase font-bold text-boosta-yellow mb-1">By campaign</div>
        <div className="font-display font-bold text-xl mb-5">Top performers</div>
        <div className="h-72">
          {campaigns.some(c => (c.spend || 0) > 0 || (c.conversions || 0) > 0) ? (
          <ResponsiveContainer>
            <BarChart data={campaigns.map(c => ({ name: c.name.slice(0, 18), spend: c.spend || 0, conversions: c.conversions || 0 }))}>
              <CartesianGrid stroke="rgba(255,255,255,0.05)" strokeDasharray="3 3"/>
              <XAxis dataKey="name" stroke="rgba(255,255,255,0.3)" fontSize={10}/>
              <YAxis stroke="rgba(255,255,255,0.3)" fontSize={11}/>
              <Tooltip contentStyle={{ background: 'rgba(8,10,18,0.95)', border: '1px solid rgba(0,240,255,0.3)', borderRadius: 12 }}/>
              <Bar dataKey="spend" fill="#00F0FF" radius={[6,6,0,0]}/>
              <Bar dataKey="conversions" fill="#E5FF00" radius={[6,6,0,0]}/>
            </BarChart>
          </ResponsiveContainer>
          ) : (
            <NoData title="No campaign results yet"
              hint="Launch a campaign and connect Meta — top performers rank here from real results." />
          )}
        </div>
      </div>
    </div>
  );
}
